import { useRouter } from 'next/router';
import { css } from '@emotion/css';
import { Col, Row } from '@prezzee/components';
import { make as Button } from '@prezzee/ribbon/src/components/personal/Button/Button.gen';
import { make as RichText } from '@prezzee/ribbon/src/components/personal/RichText/RichText.gen';
import { colors, spacing } from '@prezzee/tokens';
import { AreaOfInterest, TextChunkBlock } from 'api/graphql/generated';
import { supportingAsset } from 'api/graphql/modules/TextChunkBlock/resolvers';
import * as SegmentRe from 'bindings/Segment.bs';
import { SegmentUtils } from 'utils/SegmentUtils.bs';

import Card from './Card';
import CmsImage from 'components/CmsImage';
import useSmartLink from 'hooks/useSmartLink';

type CardGridProps = {
  block: AreaOfInterest;
};

type CardContainerProps = {
  card: TextChunkBlock;
};

const verticalMargin = 30;

const style = {
  col: css`
    margin-top: ${verticalMargin}px;
  `,
  row: css`
    margin-top: calc(-30px + -${verticalMargin}px);
    margin-bottom: 50px;
  `,
  description: css`
    margin-bottom: ${spacing.spacing04};
    color: ${colors.gray600};
  `,
  cta: css`
    align-self: flex-start;
    margin-top: auto;
  `,
};

const CardContainer = ({ card }: CardContainerProps) => {
  const { title, callToActionText, chunkBlockOfText, link, _system_ } = card;
  const { asPath } = useRouter();
  const spotlightProps = useSmartLink(_system_);

  const image = supportingAsset(card.supportingAsset);
  const openInNewWindow = card.openInNewWindow.items.length
    ? card.openInNewWindow.items[0]._system_.codename === 'yes'
      ? true
      : false
    : false;

  return (
    <Col lg={4} className={style.col} {...spotlightProps}>
      <Card>
        {image && <CmsImage {...image} sizes="28px" />}
        {title && <h3>{title}</h3>}
        {chunkBlockOfText && <RichText className={style.description}>{chunkBlockOfText.html}</RichText>}
        {link && callToActionText && (
          <a
            onClick={async () => {
              const props = {
                element: SegmentUtils.mapElement(callToActionText),
                path: asPath,
              };
              await SegmentRe.customElementInteractionAsync({ ...props });
              window.open(link, openInNewWindow ? '_blank' : '_self');
            }}
            className={style.cta}
          >
            <Button>
              <button>{callToActionText}</button>
            </Button>
          </a>
        )}
      </Card>
    </Col>
  );
};

const CardGrid = ({ block }: CardGridProps) => {
  const spotlightProps = useSmartLink(block._system_);

  return (
    <Row wrap="wrap" className={style.row} {...spotlightProps}>
      {block.addressedNeeds.items.map((card, index) => (
        <CardContainer card={card} key={index} />
      ))}
    </Row>
  );
};

export default CardGrid;
